import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import './style.css';
import { useUserStore, useUIStore } from './stores';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components that load immediately (needed on every page)
import Sidebar from './components/shared/Sidebar';
import Loading from './components/shared/Loading';

// Auth route components (needed for routing logic)
import PersistLogin from './components/auth/PersistLogin';
import RequireRiderAuth from './components/auth/RequireRiderAuth';
import RequireDriverAuth from './components/auth/RequireDriverAuth';

// Home page loads immediately (most common entry point)
import Home from './pages/public-pages/Home';

// Lazy-loaded pages (load on demand)
const AvailableDrivers = lazy(() => import('./pages/public-pages/AvailableDrivers'));
const Unauthorized = lazy(() => import('./pages/public-pages/Unauthorized'));
const ResetPassword = lazy(() => import('./pages/public-pages/ResetPassword'));
const NotFound = lazy(() => import('./pages/public-pages/NotFound'));

// Ride flow pages (only needed during active ride)
const FindRide = lazy(() => import('./pages/ride-flow-pages/FindRide'));
const WaitingForRide = lazy(() => import('./pages/ride-flow-pages/WaitingForRide'));
const PickupInfo = lazy(() => import('./pages/ride-flow-pages/PickupInfo'));
const RideCanceled = lazy(() => import('./pages/ride-flow-pages/RideCanceled'));
const RideCompleted = lazy(() => import('./pages/ride-flow-pages/RideCompleted'));
const RideExpired = lazy(() => import('./pages/ride-flow-pages/RideExpired'));

// Rider pages (only needed for riders)
const RiderHome = lazy(() => import('./pages/rider-pages/RiderHome'));
const RiderFavoriteDrivers = lazy(() => import('./pages/rider-pages/RiderFavoriteDrivers'));

// Driver pages (only needed for drivers)
const DriverHome = lazy(() => import('./pages/driver-pages/DriverHome'));
const InviteRider = lazy(() => import('./pages/driver-pages/InviteRider'));

// Shared pages (only needed when logged in)
const UserProfile = lazy(() => import('./pages/shared-pages/UserProfile'));
const UserHistory = lazy(() => import('./pages/shared-pages/UserHistory'));

/*
TODO: General TODOs:
1.) use isAuth and userType to make the private routes for either users or drivers that are logged in.
2.) Configure the web socket so that only users and drivers can communicate, and that the messages sent between a driver and a user are only visiable to the 2 of them.
3.) Find a way to track the number of times a driver has both accepted a ride and possibly completed a ride, and this will be how we charge them.
4.) Consider what else can be useContext - A lot of prop drilling might be avoidable.
5.) Search through and TODO the other TODOs!
*/

/**
 * Redirects /signup?invite=xxx to Home with state to auto-open signup modal.
 * Preserves the query string so the Signup component can read the invite code.
 */
function SignupRedirect() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const destination = inviteCode ? `/?invite=${inviteCode}` : '/';
  return <Navigate to={destination} state={{ openSignup: true }} replace />;
}

function App() {
  /* On mount:
    Connect to socket - This will connect if the server is live on going to any of the web pages, and will disconnect when the user leaves the website (unmounts the App.js page).
    Fetch driver data - This will fill the driver array state with the driver's information from the backend.
    Check for token - Checks if a user recently logged in and if so, save them time by not requiring them to re-login their information.
   */

  // Global state values.
  const { userType } = useUserStore();
  const { sidebarCollapsed } = useUIStore();

  // Determine the background color based on user type
  const bgColor = userType === 'driver' ? 'bg-main-blue-dark' : 'bg-main-green-light';

  return (
    <Router>
      <div className="min-h-screen">
        <Sidebar />

        {/* Main content area */}
        <main
          className={`min-h-screen transition-all duration-200 ${bgColor} ${
            sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
          }`}
        >
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/signup" element={<SignupRedirect />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/availabledrivers" element={<AvailableDrivers />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Protected Rider Routes.
                  NOTE: the ride-flow pages used to be public for guest
                  (anonymous) rides — that flow is disabled for the
                  invite-only alpha (tracked as a future feature), and
                  guarding these routes also gives them token refresh on
                  reload via PersistLogin. */}
              <Route element={<PersistLogin />}>
                <Route element={<RequireRiderAuth />}>
                  <Route path="/riderhome" element={<RiderHome />} />
                  <Route
                    path="/favoritedrivers"
                    element={<RiderFavoriteDrivers />}
                  />
                  {/* <Route path="/riderprofile" element={<RiderProfile />} /> */}
                  <Route path="/riderprofile" element={<UserProfile />} />
                  <Route path="/riderhistory" element={<UserHistory />} />
                  <Route path="/findride" element={<FindRide />} />
                  <Route path="/waitingforride" element={<WaitingForRide />} />
                  <Route path="/pickupinfo" element={<PickupInfo />} />
                  <Route path="/ridecanceled" element={<RideCanceled />} />
                  <Route path="/ridecompleted" element={<RideCompleted />} />
                  <Route path="/rideexpired" element={<RideExpired />} />
                </Route>

                {/* Protected Driver Routes */}
                <Route element={<RequireDriverAuth />}>
                  <Route path="/driverhome" element={<DriverHome />} />
                  <Route path="/inviterider" element={<InviteRider />} />
                  {/* <Route path="/driverprofile" element={<DriverProfile />} /> */}
                  <Route path="/driverprofile" element={<UserProfile />} />
                  <Route path="/driverhistory" element={<UserHistory />} />
                </Route>
              </Route>

              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>

        {/* ToastContainer (Notification Display) */}
        <ToastContainer position="top-right" autoClose={5000} />
      </div>
    </Router>
  );
}

export default App;
